import {ItemCode, WarehouseCode} from "./api/types"
import {SalesOrder, SalesOrderLine} from "./api/salesorder"
import {ProjectedStock} from "./projectedstock"
import {AllDataDone} from "./alldata"
import {ISODate} from "./date"
import {NEVER} from "./constants"
import utils from "./utils"
import {tuple, compare as tupleCompare} from "./tuple"
import {TSAssertType} from "./tsmagic"

const ALWAYS = new ISODate(2000, 1, 1)

class StockOnDate {
  public readonly itemCode: ItemCode
  public readonly warehouseCode: WarehouseCode
  public readonly startStock: number
  public readonly stockDiffOnDate: Map<ISODate, number>

  constructor(projectedStock: ProjectedStock,
    salesOrderLinesToIgnore: readonly SalesOrderLine[]
  ) {
    this.itemCode = projectedStock.itemCode
    this.warehouseCode = projectedStock.warehouseCode
    let lastStock = projectedStock.currentRealStock
    this.startStock = lastStock
    this.stockDiffOnDate = new Map(
      [...projectedStock.projection.values()]
      .sort(utils.By(psod => psod.date, ISODate.compare))
      .map((psod): [ISODate, number] => {
        const diff = psod.eodProjectedRealStock - lastStock
        lastStock += diff
        return [psod.date, diff]
      })
    )
    this.removePositiveSalesOrderLines(salesOrderLinesToIgnore)
  }

  private removePositiveSalesOrderLines(
    salesOrderLines: readonly SalesOrderLine[],
  ) {
    salesOrderLines
      .filter(sol => sol.parent.warehouseCode === this.warehouseCode)
      .filter(sol => sol.itemCode === this.itemCode)
      .forEach(sol => {
        this.update(sol.deliveryDate, sol.quantity)
      })
  }

  update(date: ISODate, diff: number) {
    const newDiff = (this.stockDiffOnDate.get(date) ?? 0) + diff
    if (newDiff === 0) {
      this.stockDiffOnDate.delete(date)
    } else {
      this.stockDiffOnDate.set(date, newDiff)
    }
  }

  getFirstDateWithAvailableStock(stock: number): ISODate {
    let currentStock = [...this.stockDiffOnDate.values()].reduce(
      (a, b) => a + b, this.startStock)

    if (currentStock < stock) {
      return NEVER
    }

    const datesCountDown =
      [...this.stockDiffOnDate.keys()].sort(ISODate.compare).reverse()
    for (const date of datesCountDown) {
      currentStock -= this.stockDiffOnDate.get(date)!
        if (currentStock < stock) {
          return date
        }
    }
    // still here, so we seem to always have enough
    return ALWAYS
  }

  getAvailableStockOn(requestedDate: ISODate) {
    return [...this.stockDiffOnDate.entries()]
      .sort(utils.By(entry => entry[0], ISODate.compare))
      .reduce(([availableStockOnRequestedDate, realStockOnDate], [date, diff]) => {
        /**
         * Two counters: until the requestedDate, both just count stock.
         * Beyond requested date: first one gets decreased if last one is lower than
         * first (which is how availableStock works)
         */
        const newRealStockOnDate = realStockOnDate + diff
        if (date.le(requestedDate)) {
          return [newRealStockOnDate, newRealStockOnDate]
        }
        return [
          Math.min(availableStockOnRequestedDate, newRealStockOnDate),
          newRealStockOnDate
        ]
      }, [this.startStock, this.startStock])[0]
  }

  dump(): string {
    let stock = this.startStock
    return `${this.startStock}; ` +
      [...this.stockDiffOnDate.entries()].sort(
        utils.By(entry => entry[0], ISODate.compare))
      .map(([date, diff]) => {
        stock += diff
        return `${date.toISOString()}: ${diff}(=${stock})`
      }).join("; ")
  }
}

export class SOToolLineResult {
  constructor(
    public readonly salesOrderLine: SalesOrderLine,
    public readonly deliveryDate: ISODate,
    public readonly availableStockOnRequestedDate: number,
    public readonly delayDaysBecauseOfNonPartialShipment: number,
  ) {}

  hasChanges(): boolean {
    return this.salesOrderLine.deliveryDate !== this.deliveryDate
  }

  update(
    fields: {deliveryDate?: ISODate, delayDaysBecauseOfNonPartialShipment?: number}
  ): SOToolLineResult {
    return new SOToolLineResult(
      this.salesOrderLine,
      fields.deliveryDate ?? this.deliveryDate,
      this.availableStockOnRequestedDate,
      fields.delayDaysBecauseOfNonPartialShipment ??
      this.delayDaysBecauseOfNonPartialShipment)
  }

  getDeliveryDate(prePost: PrePost): ISODate {
    return prePost === "pre" ? this.salesOrderLine.deliveryDate : this.deliveryDate
  }
}

export type PrePost = "pre" | "post"

export class SOToolResult {
  constructor (
    public readonly salesOrder: SalesOrder,
    public readonly lines: readonly SOToolLineResult[],
  ){}

  hasChanges(): boolean {
    return this.lines.some(line => line.hasChanges())
  }

  private _getDeliveryDateGetter(
    prePost: PrePost
  ): (line: SOToolLineResult) => ISODate {
    return prePost === "pre"
      ? line => line.salesOrderLine.deliveryDate
      : line => line.deliveryDate
  }

  getOrderDelayDays(prePost: PrePost): number {
    /**
     * returns how many days this order will be be shipped later than requested
     */
    const lastDeliveryDate = this.getEverythingDeliveredDate(prePost)
    if (lastDeliveryDate.ge(NEVER)) {
      return Infinity;
    }
    return this.salesOrder.deliveryDate.getDaysUntil(lastDeliveryDate)
  }

  getEverythingDeliveredDate(prePost: PrePost): ISODate {
    const deliveryDateGetter = this._getDeliveryDateGetter(prePost)
    return ISODate.latest(this.lines.map(deliveryDateGetter))
  }

  getSalesOrdersByDeliveryDate(
    prePost: PrePost
  ): Map<ISODate, ReadonlyArray<SalesOrderLine>> {
    const deliveryDateGetter = this._getDeliveryDateGetter(prePost)
    return utils.intoBucketsTransform(
      this.lines, deliveryDateGetter, line => line.salesOrderLine)
  }

  getNumberOfPartialShipments(prePost: PrePost): number {
    const deliveryDateGetter = this._getDeliveryDateGetter(prePost)
    return new Set(this.lines.map(deliveryDateGetter)).size
  }

  isFulfillable(prePost: PrePost): boolean {
    const deliveryDateGetter = this._getDeliveryDateGetter(prePost)
    return this.lines.map(deliveryDateGetter).every(date => date.lt(NEVER))
  }
}

function getSOToolLineResultAndUpdateStockOnDate(
  stockOnDate: StockOnDate,
  salesOrderLine: SalesOrderLine,
  firstPossibleDeliveryDate: ISODate,
): SOToolLineResult {
  const quantityNeeded = salesOrderLine.quantity
  utils.assert(quantityNeeded > 0,"Quantity not positive: PO:"
    + salesOrderLine.getOrderNumberLineNumber())
  const availableDate = stockOnDate.getFirstDateWithAvailableStock(quantityNeeded)
  const requestedDate = salesOrderLine.parent.deliveryDate
  const resolvedDate =
    ISODate.latest([availableDate, requestedDate, firstPossibleDeliveryDate])
  const availableStockOnRequestedDate =
    stockOnDate.getAvailableStockOn(requestedDate)
  stockOnDate.update(resolvedDate, -quantityNeeded)
  return new SOToolLineResult(
    salesOrderLine,
    resolvedDate,
    availableStockOnRequestedDate,
    /* delay */ 0)
  }


function getSOToolResult(
  salesOrder: SalesOrder,
  salesOrderLines: readonly SalesOrderLine[],
  stockOnDateByItemCode: ReadonlyMap<ItemCode, StockOnDate>,
  firstPossibleDeliveryDate: ISODate,
): SOToolResult {
  utils.assert(salesOrderLines.every(sol => sol.parent === salesOrder))
  utils.assert(salesOrderLines.every(
    sol => stockOnDateByItemCode.has(sol.itemCode)))
  const solCompare = utils.By(
    (sol: SalesOrderLine) => tuple([sol.lineNumber, sol.iD]), tupleCompare)

  salesOrderLines = [...salesOrderLines].sort(solCompare)
  const soToolLineResults = salesOrderLines.map(
    sol => getSOToolLineResultAndUpdateStockOnDate(
      stockOnDateByItemCode.get(sol.itemCode)!, sol, firstPossibleDeliveryDate))
  let partialShipmentDate: ISODate | null = null
  const soToolLineResultsWithPartialShipmentRules = [...soToolLineResults]
    .sort(utils.By(sotlr => sotlr.deliveryDate, ISODate.compare))
    .reverse()
    .map(sotlr => {
      let delayDaysBecauseOfNonPartialShipment =
        partialShipmentDate === null ? null :
        sotlr.deliveryDate.getDaysUntil(partialShipmentDate)

      if (delayDaysBecauseOfNonPartialShipment === null || (
        delayDaysBecauseOfNonPartialShipment >
        // new partial shipment
        salesOrder.$calc.partialShipmentMaxDelayDays &&
        sotlr.salesOrderLine.$calc.triggerPartialShipment)
      ) {
        partialShipmentDate = sotlr.deliveryDate
        delayDaysBecauseOfNonPartialShipment = 0
      }
      TSAssertType<ISODate>(partialShipmentDate)
      if (delayDaysBecauseOfNonPartialShipment !== 0) {
        const stockOnDate =
          stockOnDateByItemCode.get(sotlr.salesOrderLine.itemCode)!
        const orderQuantity = sotlr.salesOrderLine.quantity
        // restock the items on the original deliveryDate
        stockOnDate.update(sotlr.deliveryDate, orderQuantity)

        // un-restock the items on the new deliveryDate
        stockOnDate.update(partialShipmentDate, -orderQuantity)
      }
      return sotlr.update({
        deliveryDate: partialShipmentDate,
        delayDaysBecauseOfNonPartialShipment
      })
    })
  .sort(utils.By(sotlr => sotlr.salesOrderLine, solCompare))
  return new SOToolResult(
    salesOrder,
    soToolLineResultsWithPartialShipmentRules
  )
}

function sortSalesOrders(
  salesOrders: readonly SalesOrder[]): readonly SalesOrder[] {
    return [...salesOrders].sort(utils.By(
      so => tuple([
        -so.$calc.priority,
        so.$calc.fictionalOrderDate ?? so.orderDate,
        so.orderNumber
      ]), tupleCompare))
  }

export function getSOToolResults(
  warehouseCode: WarehouseCode,
  allData: AllDataDone,
  firstPossibleDeliveryDate: ISODate,
): readonly SOToolResult[] {
  const soTooSalesOrderLines = allData.salesOrderLines.filter(
    sol => sol.quantity > 0)
  const stockOnDateByItemCode = new Map<ItemCode, StockOnDate>(
    allData.projectedStocks
    .filter(ps => ps.warehouseCode === warehouseCode)
    .map(ps => [ps.itemCode, new StockOnDate(ps, allData.salesOrderLines)]))
  const salesOrders = sortSalesOrders(
    allData.salesOrders.filter(so => so.warehouseCode === warehouseCode))
  return salesOrders.map(so => {
    const salesOrderLines = soTooSalesOrderLines.filter(sol => sol.parent === so)
    if (salesOrderLines.length === 0) {
      return null
    }
    return getSOToolResult(
      so, salesOrderLines, stockOnDateByItemCode, firstPossibleDeliveryDate)
  }).filter(utils.notNullOrUndefined)
}

